/**
 * PendingApprovalsWidget — „Czekające decyzje" = moje czekające decyzje w procesach (kroki
 * przypisane do mnie); to NIE jest lista zadań z kartoteki Zadania. Reużywa endpoint listingu
 * procesów (`my-pending-decisions`) + resolver URL rekordu. Klik pozycji → drill-down do karty
 * rekordu (wzorzec ADR-0040).
 *
 * Układ: podsumowanie „N do podjęcia · M po terminie", wiersze z kółkiem
 * statusu, tytułem + podtytułem, chipem terminu (po terminie / dziś / data) i avatarem
 * zgłaszającego; priorytet „Pilne" jako czerwony znacznik.
 *
 * Gdy moduł procesów nieaktywny backend zwróci błąd — degradujemy do łagodnego stanu
 * (kafelek opcjonalny, jak bramkowane KPI).
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { ChevronRight, Circle } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { useCanManageProcessDefinitions } from '@/hooks/useCanManageProcessDefinitions';
import { fetchMyPendingDecisions, type MyPendingDecisionRow } from '@/lib/processes/approvals-api';
import { isTargetNavigable } from '@/lib/processes/module-routes';
import { useNavigateToTarget } from '@/lib/processes/use-navigate-to-target';

import type { WidgetRenderProps } from '../types';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

const DAY_MS = 86_400_000;

export type DueKind = 'overdue' | 'today' | 'later' | 'none';

export interface DueInfo {
  kind: DueKind;
  /** Dni po terminie (tylko `overdue`, ≥ 1). */
  days: number;
}

/** Klasyfikuje termin wniosku względem „teraz": po terminie / dziś / później / brak. */
export function dueInfo(deadlineAt: string | null, now: Date = new Date()): DueInfo {
  if (!deadlineAt) return { kind: 'none', days: 0 };
  const due = new Date(deadlineAt);
  if (Number.isNaN(due.getTime())) return { kind: 'none', days: 0 };
  if (due.getTime() < now.getTime()) {
    const sameDay = due.toDateString() === now.toDateString();
    return sameDay
      ? { kind: 'today', days: 0 }
      : {
          kind: 'overdue',
          days: Math.max(1, Math.floor((now.getTime() - due.getTime()) / DAY_MS)),
        };
  }
  return due.toDateString() === now.toDateString()
    ? { kind: 'today', days: 0 }
    : { kind: 'later', days: 0 };
}

function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts.length > 1 ? parts[parts.length - 1]![0]! : '')).toUpperCase();
}

export function PendingApprovalsWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const canManageProcessDefinitions = useCanManageProcessDefinitions();
  const navigateToTarget = useNavigateToTarget();

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'pending-approvals'],
    queryFn: () => fetchMyPendingDecisions({ limit: 8 }),
    enabled: widget.type === 'pending_approvals',
    staleTime: 30_000,
    retry: false,
  });

  if (widget.type !== 'pending_approvals') return null;

  if (query.isLoading) return <WidgetLoading />;
  if (query.isError) {
    // 402 = bramka modułu Procesy nieaktywna → łagodna degradacja (nie błąd z retry).
    const status = (query.error as AxiosError | undefined)?.response?.status;
    if (status === 402)
      return (
        <WidgetEmpty message={t('tenant.dashboard.widgets.pending_approvals.moduleInactive')} />
      );
    return <WidgetDataError kind="error" onRetry={() => query.refetch()} />;
  }

  const rows: MyPendingDecisionRow[] = query.data?.data ?? [];
  if (rows.length === 0)
    return <WidgetEmpty message={t('tenant.dashboard.widgets.pending_approvals.empty')} />;

  const now = new Date();
  const overdueCount = rows.filter((r) => dueInfo(r.deadlineAt, now).kind === 'overdue').length;

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2 text-xs text-text-tertiary">
        <span className="font-medium text-text-secondary">
          {t('tenant.dashboard.widgets.pending_approvals.count', { count: rows.length })}
        </span>
        {overdueCount > 0 && (
          <Badge tone="danger" size="sm">
            {t('tenant.dashboard.widgets.pending_approvals.overdue', { count: overdueCount })}
          </Badge>
        )}
      </div>

      <ul className="flex min-h-0 flex-1 flex-col divide-y divide-border-secondary overflow-auto">
        {rows.map((row) => {
          const navigable = isTargetNavigable(row.targetModuleCode, row.targetRecordId);
          const label = row.targetLabel || row.processName || `#${row.instanceNumber}`;
          const sub = row.currentNodeLabel || row.processName || '';
          const due = dueInfo(row.deadlineAt, now);
          const submitter = row.submittedByUser?.displayName || row.submittedByUser?.email || null;
          const content = (
            <>
              <Circle className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="min-w-0 truncate text-[13px] text-text-primary" title={label}>
                    {label}
                  </span>
                  {row.priority === 'urgent' && (
                    <Badge tone="danger" size="sm">
                      {t('tenant.dashboard.widgets.pending_approvals.priority.urgent')}
                    </Badge>
                  )}
                </div>
                {sub && <div className="truncate text-[11px] text-text-tertiary">{sub}</div>}
              </div>
              {due.kind === 'overdue' && (
                <Badge tone="danger" size="sm">
                  {t('tenant.dashboard.widgets.pending_approvals.overdueDays', { count: due.days })}
                </Badge>
              )}
              {due.kind === 'today' && (
                <Badge tone="info" size="sm">
                  {t('tenant.dashboard.widgets.pending_approvals.dueToday', {
                    time: new Date(row.deadlineAt!).toLocaleTimeString('pl-PL', {
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  })}
                </Badge>
              )}
              {due.kind === 'later' && (
                <Badge tone="neutral" size="sm">
                  {new Date(row.deadlineAt!).toLocaleDateString('pl-PL', {
                    day: '2-digit',
                    month: '2-digit',
                  })}
                </Badge>
              )}
              {submitter && (
                <span
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-[10px] font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                  title={submitter}
                  aria-label={submitter}
                >
                  {initials(submitter)}
                </span>
              )}
            </>
          );
          return (
            <li key={row.stepInstanceId} className="py-2">
              {navigable ? (
                // Drill-down do kartoteki celu. Nie `<Link>` (dla dokumentu adres zależy od klasy,
                // rozwiązywanej dopiero na kliknięciu — patrz `useNavigateToTarget`), więc klikalny
                // element z rolą przycisku + obsługą klawiatury. `onMouseDown` stopPropagation —
                // widget bywa w przeciąganej siatce pulpitu, klik nie ma startować dragu.
                <div
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => navigateToTarget(row.targetModuleCode, row.targetRecordId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigateToTarget(row.targetModuleCode, row.targetRecordId);
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1 hover:bg-bg-secondary"
                >
                  {content}
                </div>
              ) : (
                <div className="flex items-center gap-2.5 px-1">{content}</div>
              )}
            </li>
          );
        })}
      </ul>

      {canManageProcessDefinitions && (
        <Link
          to="/processes"
          onMouseDown={(e) => e.stopPropagation()}
          className="inline-flex shrink-0 items-center justify-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700"
        >
          {t('tenant.dashboard.widgets.pending_approvals.viewAll')}
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}
